import { Menu, Transition } from "@headlessui/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DateRangePicker as RangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useRangePicker } from "../store/use-range-picker-model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";

interface IDateRangePicker {
  children: React.ReactNode;
  onChange: (e: Record<string, any>) => void;
}

const DateRangePicker = ({ children, onChange }: IDateRangePicker) => {
  const { initialValues, onCloseRangePicker } = useRangePicker();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
    new: true,
  });

  useLayoutEffect(() => {
    if (!initialValues.isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + 10,
      right: window.innerWidth - rect.right,
    });
  }, [initialValues.isOpen]);

  useEffect(() => {
    if (!initialValues.isOpen) return;
    const handler = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 10,
        right: window.innerWidth - rect.right,
      });
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [initialValues.isOpen]);

  const handleSelect = (e) => {
    const dataSelection = {
      startDate: e.selection?.startDate?.toISOString(),
      endDate:
        e.selection?.startDate === e.selection?.endDate
          ? null
          : e.selection?.endDate?.toISOString(),
      key: "selection",
    };

    onChange(dataSelection);
    setDateRange({ ...e.selection, new: false });
  };

  const resetFilter = () => {
    const dataSelection = {
      startDate: null,
      endDate: null,
      key: "selection",
    };

    onChange(dataSelection);
    setDateRange({
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
      new: true,
    });
  };

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 640;

  const panel =
    typeof document !== "undefined"
      ? createPortal(
          <Transition
            as="div"
            className="fixed inset-0 z-[9999] pointer-events-none"
            enter="transition ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            show={initialValues.isOpen}
          >
            <div
              className="absolute inset-0 sm:hidden bg-black/40 backdrop-blur-sm pointer-events-auto"
              onClick={onCloseRangePicker}
            />
            <div
              className="hidden sm:block absolute inset-0 pointer-events-auto"
              onClick={onCloseRangePicker}
            />

            <div
              style={
                isMobile
                  ? undefined
                  : { top: panelPos.top, right: panelPos.right }
              }
              className="
                absolute
                left-1/2 sm:left-auto top-1/2 sm:top-auto
                -translate-x-1/2 -translate-y-1/2 sm:translate-x-0 sm:translate-y-0
                w-[min(95vw,360px)] sm:w-auto
                p-3 sm:p-4
                rounded-2xl border border-edge shadow-card
                flex items-center justify-center
                bg-surface-elevated
                animate-fade-in
                overflow-hidden
                pointer-events-auto
              "
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-h-[80vh] overflow-y-auto custom-scrollbar">
                <RangePicker
                  ranges={[dateRange]}
                  onChange={handleSelect}
                  rangeColors={["#6366F1"]}
                  showMonthAndYearPickers={true}
                />
              </div>
            </div>
          </Transition>,
          document.body,
        )
      : null;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div className="flex items-center gap-2" ref={triggerRef}>
        <Menu.Button data-bs-toggle="dropdown" className="cursor-pointer">
          {children}
        </Menu.Button>
        {!dateRange.new && (
          <div
            onClick={resetFilter}
            className="cursor-pointer px-4 rounded-full appearance-none text-[16px] flex items-center justify-center h-[50px] bg-surface-elevated text-light-gray border border-edge outline-none hover:border-indigo-400/40 transition-all duration-300"
          >
            <FontAwesomeIcon icon={faCalendarXmark} />
          </div>
        )}
      </div>

      {panel}
    </Menu>
  );
};

export default DateRangePicker;
